import React from 'react';

/**
 * Visual-only overhead band (fixed-sash, fixed-glass, tilt types) — shared by door and side-panel overhead.
 */
export function renderOverheadOpeningVisual({
  openingX,
  openingY,
  openingW,
  openingH,
  windowType,
  scale,
  frameThickness,
  frameEdgeStroke,
  glazingBeadFill,
  woodJoint,
  glassFill,
  glassTexturePattern,
  hasInnerFrame,
  renderOverheadFixedSashOutline,
  renderOverheadInnerSashStiles,
  renderOverheadInnerSashBorderlines,
  renderOverheadInnerSashBottomBorders,
  /** Optional solid fill for Vast Panel (falls back to panel-gradient). */
  panelFill,
}) {
  const borderT = 2 * scale;
  const fixedPanelFill = panelFill || 'url(#panel-gradient)';

  if (!hasInnerFrame(windowType)) {
    const glassX = openingX + borderT;
    const glassY = openingY + borderT;
    const glassW = Math.max(0, openingW - borderT * 2);
    const glassH = Math.max(0, openingH - borderT * 2);
    return (
      <g pointerEvents="none">
        {renderOverheadFixedSashOutline(openingX, openingY, openingW, openingH)}
        <rect
          x={glassX}
          y={glassY}
          width={glassW}
          height={glassH}
          fill={windowType === 'fixed-panel' ? fixedPanelFill : glassFill}
        />
        {windowType !== 'fixed-panel' && glassTexturePattern && (
          <rect x={glassX} y={glassY} width={glassW} height={glassH} fill={glassTexturePattern} opacity="1" />
        )}
      </g>
    );
  }

  const frameT = frameThickness * 0.7;
  const innerX = openingX;
  const innerY = openingY;
  const innerW = openingW;
  const innerH = openingH;
  const glassX = innerX + frameT + 2 * scale;
  const glassY = innerY + frameT + 2 * scale;
  const glassW = innerW - frameT * 2 - 4 * scale;
  const glassH = innerH - frameT * 2 - 4 * scale;

  const tiltLines = (() => {
    if (windowType === 'tilt-only') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    if (windowType === 'tilt-turn-left') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY} x2={glassX} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    if (windowType === 'tilt-turn-right') {
      return (
        <g pointerEvents="none">
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX + glassW} y1={glassY + glassH} x2={glassX + glassW / 2} y2={glassY} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX} y1={glassY} x2={glassX + glassW} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
          <line x1={glassX} y1={glassY + glassH} x2={glassX + glassW} y2={glassY + glassH / 2} stroke="#999999" strokeWidth="3" strokeDasharray="3" />
        </g>
      );
    }
    return null;
  })();

  return (
    <g style={{ pointerEvents: 'none' }}>
      {renderOverheadInnerSashStiles(innerX, innerY, innerW, innerH, frameT)}
      {woodJoint === 'joint-45' ? (
        <>
          <line x1={innerX} y1={innerY} x2={innerX + frameT} y2={innerY + frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW} y1={innerY} x2={innerX + innerW - frameT} y2={innerY + frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX} y1={innerY + innerH} x2={innerX + frameT} y2={innerY + innerH - frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW} y1={innerY + innerH} x2={innerX + innerW - frameT} y2={innerY + innerH - frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
        </>
      ) : (
        <>
          <line x1={innerX} y1={innerY + frameT} x2={innerX + frameT} y2={innerY + frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW - frameT} y1={innerY + frameT} x2={innerX + innerW} y2={innerY + frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX} y1={innerY + innerH - frameT} x2={innerX + frameT} y2={innerY + innerH - frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX} y1={innerY + innerH - frameT} x2={innerX} y2={innerY + innerH} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW - frameT} y1={innerY + innerH - frameT} x2={innerX + innerW} y2={innerY + innerH - frameT} stroke={frameEdgeStroke} strokeWidth="1.2" />
          <line x1={innerX + innerW} y1={innerY + innerH - frameT} x2={innerX + innerW} y2={innerY + innerH} stroke={frameEdgeStroke} strokeWidth="1.2" />
        </>
      )}
      {renderOverheadInnerSashBorderlines(innerX, innerY, innerW, innerH, frameT)}
      <g pointerEvents="none">
        <rect x={innerX + frameT + 2 * scale} y={innerY + frameT} width={innerW - frameT * 2 - 4 * scale} height={2 * scale} fill={glazingBeadFill} />
        <rect x={innerX + frameT + 2 * scale} y={innerY + innerH - frameT - 2 * scale} width={innerW - frameT * 2 - 4 * scale} height={2 * scale} fill={glazingBeadFill} />
        <rect x={innerX + frameT} y={innerY + frameT} width={2 * scale} height={innerH - frameT * 2} fill={glazingBeadFill} />
        <rect x={innerX + innerW - frameT - 2 * scale} y={innerY + frameT + 2 * scale} width={2 * scale} height={innerH - frameT * 2 - 4 * scale} fill={glazingBeadFill} />
      </g>
      <rect x={glassX} y={glassY} width={glassW} height={glassH} fill="url(#glass-gradient)" />
      {glassTexturePattern && (
        <rect x={glassX} y={glassY} width={glassW} height={glassH} fill={glassTexturePattern} opacity="1" />
      )}
      {renderOverheadInnerSashBottomBorders(innerX, innerY, innerW, innerH, frameT)}
      {tiltLines}
    </g>
  );
}
